import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Activity,
  Battery,
  WifiOff,
  Zap,
  Thermometer,
  PowerOff,
  Cpu,
  ShieldAlert,
  CheckCircle2,
  Bell,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { alarmsAPI } from '../utils/api';
import { toast } from 'sonner';

/* ─── Util fns ─────────────────────────────────────────────────── */
const _asUtcDate = (iso) => {
  if (!iso) return null;
  const s = String(iso);
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  return new Date(s.replace(' ', 'T') + 'Z');
};

const fmtRelative = (iso) => {
  const d = _asUtcDate(iso);
  if (!d || isNaN(d.getTime())) return 'just now';
  const seconds = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return `${Math.floor(seconds)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m ago`;
  }
  return `${Math.floor(seconds / 86400)}d ago`;
};

/* ─── Alarm-type → icon ────────────────────────────────────────── */
const _ICONS = {
  grid_outage: PowerOff,
  bess_not_discharging: AlertTriangle,
  backup_not_responding: AlertTriangle,
  comms_lost: WifiOff,
  low_soc: Battery,
  demand_exceedance: Zap,
  high_temp: Thermometer,
  device_feed_stale: Cpu,
  grid_source_disagreement: ShieldAlert,
};

const _SEV_STYLE = {
  critical: {
    band: 'border-l-rose-500 bg-rose-50/40',
    chip: 'bg-rose-100 text-rose-700 border-rose-200',
    icon: 'text-rose-600 bg-rose-50',
    label: 'CRITICAL',
  },
  warning: {
    band: 'border-l-amber-500 bg-amber-50/40',
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: 'text-amber-600 bg-amber-50',
    label: 'WARNING',
  },
  info: {
    band: 'border-l-sky-500 bg-sky-50/40',
    chip: 'bg-sky-100 text-sky-700 border-sky-200',
    icon: 'text-sky-600 bg-sky-50',
    label: 'INFO',
  },
};

/* ─── Single alarm row ─────────────────────────────────────────── */
function AlarmRow({ alarm, onAck, onView }) {
  const sev = (alarm.severity || 'info').toLowerCase();
  const s = _SEV_STYLE[sev] || _SEV_STYLE.info;
  const Icon = _ICONS[alarm.alarm_type] || AlertCircle;
  const ageMs = Date.now() - (_asUtcDate(alarm.timestamp || alarm.created_at)?.getTime() || Date.now());
  const isAck = !!alarm.acknowledged;
  const shouldPulse = !isAck && ageMs > 30 * 60 * 1000; // > 30 min unacked

  return (
    <div
      className={`relative border-l-4 ${s.band} px-4 py-3 transition-opacity ${isAck ? 'opacity-60' : ''}`}
      data-testid={`alarm-row-${alarm.alarm_id}`}
    >
      {shouldPulse && (
        <span
          className="absolute top-3 right-3 w-2 h-2 rounded-full bg-rose-500 animate-ping"
          data-testid="alarm-pulse"
        />
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start space-x-3 min-w-0">
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${s.icon}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span
                className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${s.chip}`}
              >
                {s.label}
              </span>
              <h4 className="font-semibold text-slate-900 text-sm truncate" data-testid="alarm-title">
                {alarm.title || alarm.alarm_type}
              </h4>
            </div>
            <p className="text-xs text-slate-600 leading-snug">{alarm.description}</p>
            <div className="text-[11px] text-slate-500 mt-1">
              {fmtRelative(alarm.timestamp || alarm.created_at)}
              {isAck && (
                <>
                  {' · '}
                  <span className="text-emerald-700 font-medium">acknowledged</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => onView(alarm)}
            className="text-[11px] px-2.5 py-1 border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-center gap-1"
            data-testid="alarm-view-site-btn"
          >
            <ExternalLink className="w-3 h-3" /> View site
          </button>
          {!isAck && (
            <button
              type="button"
              onClick={() => onAck(alarm)}
              className="text-[11px] px-2.5 py-1 bg-slate-900 text-white rounded-sm hover:bg-slate-800 transition-colors"
              data-testid="alarm-ack-btn"
            >
              Acknowledge
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── All-systems-normal state ─────────────────────────────────── */
function AllSystemsNormal({ siteStatus }) {
  const totalSites = (siteStatus || []).length;
  const sample = (siteStatus || []).find((s) => s.grid_status === 'up' && s.soc_percent != null) || (siteStatus || [])[0];
  const lastSeen = sample?.latest_telemetry_at ? fmtRelative(sample.latest_telemetry_at) : '—';
  return (
    <div
      className="border-l-4 border-l-emerald-500 bg-emerald-50/40 px-4 py-4 flex items-center justify-between"
      data-testid="all-systems-normal"
    >
      <div className="flex items-center space-x-3">
        <div className="w-9 h-9 rounded-sm flex items-center justify-center bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div>
          <div className="font-semibold text-emerald-900 text-sm">All systems normal</div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            {totalSites} site{totalSites !== 1 ? 's' : ''} monitored
            {sample?.grid_status && <> · Grid: <span className="uppercase">{sample.grid_status}</span></>}
            {sample?.soc_percent != null && <> · SoC: {sample.soc_percent.toFixed(0)}%</>}
            {' · Last telemetry: '}{lastSeen}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main component ───────────────────────────────────────────── */
export default function ActiveAlarmsPanel({ siteStatus }) {
  const [alarms, setAlarms] = useState([]);
  const [counts, setCounts] = useState({ critical: 0, warning: 0, info: 0, total: 0 });
  const [, setRefreshTick] = useState(0);
  const navigate = useNavigate();

  const fetchAlarms = useCallback(async () => {
    try {
      const [aRes, cRes] = await Promise.all([
        alarmsAPI.getActive(),
        alarmsAPI.getCounts(),
      ]);
      setAlarms(aRes.data?.alarms || []);
      setCounts(cRes.data || { critical: 0, warning: 0, info: 0, total: 0 });
    } catch (err) {
      // silent — keep last good state
    }
  }, []);

  useEffect(() => {
    fetchAlarms();
    const it = setInterval(fetchAlarms, 30000);
    const tick = setInterval(() => setRefreshTick((t) => t + 1), 60000); // re-render ages
    return () => {
      clearInterval(it);
      clearInterval(tick);
    };
  }, [fetchAlarms]);

  const handleAck = async (alarm) => {
    try {
      await alarmsAPI.acknowledge(alarm.alarm_id);
      toast.success('Alarm acknowledged');
      setAlarms((prev) =>
        prev.map((a) => (a.alarm_id === alarm.alarm_id ? { ...a, acknowledged: true } : a)),
      );
    } catch (err) {
      toast.error('Failed to acknowledge alarm');
    }
  };

  const handleView = (alarm) => {
    if (alarm.site_id) navigate(`/sites/${alarm.site_id}`);
  };

  const grouped = useMemo(() => {
    const out = { critical: [], warning: [], info: [] };
    for (const a of alarms) {
      const sev = (a.severity || 'info').toLowerCase();
      (out[sev] || out.info).push(a);
    }
    return out;
  }, [alarms]);

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm overflow-hidden"
      data-testid="active-alarms-panel"
    >
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-slate-700" />
          <h3 className="font-heading text-sm font-semibold tracking-tight text-slate-900">
            Active Alarms
          </h3>
          {counts.total > 0 && (
            <span
              className="ml-1 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700"
              data-testid="alarm-counts-summary"
            >
              {counts.critical > 0 && (
                <span className="text-rose-700 font-semibold">{counts.critical} critical</span>
              )}
              {counts.warning > 0 && (
                <span className="text-amber-700">· {counts.warning} warning</span>
              )}
              {counts.info > 0 && <span className="text-sky-700">· {counts.info} info</span>}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/alarms')}
          className="text-[11px] text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline"
          data-testid="view-all-alarms-link"
        >
          View All →
        </button>
      </div>

      {alarms.length === 0 ? (
        <AllSystemsNormal siteStatus={siteStatus} />
      ) : (
        <div className="divide-y divide-slate-100">
          {['critical', 'warning', 'info'].map((sev) =>
            grouped[sev].length ? (
              <div key={sev} className="divide-y divide-slate-100">
                {grouped[sev].map((a) => (
                  <AlarmRow key={a.alarm_id} alarm={a} onAck={handleAck} onView={handleView} />
                ))}
              </div>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}
